import React, { useEffect, useState, useRef } from "react";
import {
    LinearProgress,
    Snackbar,
    Alert,
    CircularProgress,
} from "@mui/material";
import { useAuth } from "../../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
    institutionRegister,
    sendEmailVerificationCode,
    login,
} from "../../../../services/auth";

export default function InstitutionSignupPage() {
    const navigate = useNavigate();
    const { user, setUserData, setUserToken, setInstitutionData } = useAuth();

    const [formData, setFormData] = useState({
        entityName: "",
        adminName: "",
        email: "",
        password: "",
        verificationCode: "",
    });
    const isPasswordInvalid = formData.password.length > 0 && formData.password.length < 9;

    const [isLoading, setIsLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarContent, setSnackbarContent] = useState({});
    const [isSendingVerificationCode, setIsSendingVerificationCode] = useState(false);
    const [verificationCodeSent, setVerificationCodeSent] = useState(false);

    const firstInputRef = useRef(null);

    useEffect(() => {
        if (firstInputRef.current) {
            firstInputRef.current.focus();
        }
    }, []);

    const handleChange = (field, value) =>
        setFormData((prev) => ({ ...prev, [field]: value }));

    const validateFields = () => {
        const {
            entityName,
            adminName,
            email,
            password,
            verificationCode,
        } = formData;

        if (!entityName) return showError("يرجى تعبئة بيانات المنشأة كاملة.");
        if (!adminName) return showError("يرجى إدخال اسم المسؤول.");
        if (!email || !password) return showError("يرجى إدخال البريد الإلكتروني وكلمة المرور.");
        if (password.length < 9) return showError("كلمة المرور يجب أن تكون 9 خانات أو أكثر.");
        if (verificationCodeSent && !verificationCode.trim()) return showError("يرجى إدخال رمز التحقق.");
        return true;
    };

    const showError = (message) => {
        setSnackbarOpen(true);
        setSnackbarContent({ message, type: "error" });
        return false;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateFields()) return;

        if (!verificationCodeSent) {
            try {
                setIsSendingVerificationCode(true);
                await sendEmailVerificationCode(formData.email);
                setSnackbarContent({
                    message: `تم إرسال رمز التحقق إلى ${formData.email}`,
                    type: "success",
                });
                setVerificationCodeSent(true);
            } catch (err) {
                showError(err.message || "فشل في إرسال رمز التحقق");
            } finally {
                setSnackbarOpen(true);
                setIsSendingVerificationCode(false);
            }
            return;
        }

        try {
            setIsLoading(true);
            await institutionRegister(formData);
            const res = await login(formData.email, formData.password);

            const userData = res.data.user;
            userData.email = res.data.email;
            userData.role = res.data.user.roles[0];

            setUserData(userData);
            setInstitutionData(res.data.institution);
            setUserToken(res.data.accessToken);

            setSnackbarContent({
                message: "تم تسجيل المؤسسة وتسجيل الدخول بنجاح",
                type: "success",
            });

            navigate(`/${userData.role}`);
        } catch (err) {
            console.error(err);
            showError(err.message || "حدث خطأ أثناء التسجيل");
            setFormData((prev) => ({ ...prev, verificationCode: "" }));
        } finally {
            setIsLoading(false);
            setSnackbarOpen(true);
        }
    };

    useEffect(() => {
        if (user) navigate(`/${user.roles[0]}`);
    }, [user]);

    return (
        <div className="page">
            <div
                className="content-view"
                style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <div className="card">
                    {isLoading ? (
                        <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
                            <CircularProgress color="primary" />
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <h2>إنشاء حساب منشأة</h2>

                            {!verificationCodeSent && (
                                <>
                                    <h4 className="sub-head">بيانات المنشأة</h4>
                                    <div className="form-group">
                                        <label htmlFor="entityName" className="label">اسم المنشأة</label>
                                        <input
                                            id="entityName"
                                            ref={firstInputRef}
                                            placeholder="مؤسسة ..."
                                            value={formData.entityName}
                                            onChange={(e) => handleChange("entityName", e.target.value)}
                                        />
                                    </div>

                                    <h4 className="sub-head">بيانات المسؤول</h4>
                                    <div className="form-group">
                                        <label htmlFor="adminName" className="label">اسم المسؤول</label>
                                        <input
                                            id="adminName"
                                            placeholder="محمد صالح"
                                            value={formData.adminName}
                                            onChange={(e) => handleChange("adminName", e.target.value)}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="email" className="label">البريد الإلكتروني</label>
                                        <input
                                            id="email"
                                            type="email"
                                            placeholder="admin@email.com"
                                            value={formData.email}
                                            onChange={(e) => handleChange("email", e.target.value)}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="password" className="label">كلمة المرور</label>
                                        <input
                                            id="password"
                                            type="password"
                                            placeholder="••••••••"
                                            value={formData.password}
                                            onChange={(e) => handleChange("password", e.target.value)}
                                            style={{
                                                borderColor: isPasswordInvalid ? "red" : undefined,
                                            }}
                                        />
                                        {isPasswordInvalid && (
                                            <div style={{ color: "red", fontSize: "13px", marginTop: "5px" }}>
                                                كلمة المرور يجب أن تكون على الأقل 9 خانات
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {verificationCodeSent && (
                                <div>
                                    <div className="form-group">
                                        <label htmlFor="verificationCode" className="label">رمز التحقق</label>
                                        <input
                                            id="verificationCode"
                                            type="text"
                                            autoFocus={verificationCodeSent}
                                            value={formData.verificationCode}
                                            onChange={(e) => handleChange("verificationCode", e.target.value)}
                                        />
                                    </div>
                                    <div className="form-group" style={{ textAlign: "right", color: "green", fontSize: "14px" }}>
                                        تم إرسال رمز التحقق إلى {formData.email}
                                    </div>
                                </div>
                            )}

                            {isSendingVerificationCode && <LinearProgress color="primary" />}

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="btn filled"
                                style={{ width: "100%", padding: "10px 0", fontSize: "15px", marginTop: "1.5rem" }}
                            >
                                {verificationCodeSent ? "تسجيل" : "إرسال رمز التحقق"}
                            </button>
                        </form>
                    )}
                </div>

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={snackbarContent.type === "error" ? null : 6000}
                    onClose={() => setSnackbarOpen(false)}
                >
                    <Alert
                        onClose={() => setSnackbarOpen(false)}
                        severity={snackbarContent.type}
                        variant="filled"
                        sx={{ width: "100%" }}
                    >
                        {snackbarContent.message}
                    </Alert>
                </Snackbar>
            </div>

            <style jsx>{`
                .sub-head {
                    margin: 1.2rem 0 1rem;
                    font-size: 15px;
                    color: var(--clr-primary);
                    text-align: right;
                    border-bottom: 1px solid #01c8aa;
                    padding-bottom: 5px;
                    width: fit-content;
                }

                .form-group {
                    margin-bottom: 1rem;
                    text-align: right;
                }

                .row {
                    display: flex;
                    gap: 1rem;
                    margin-bottom: 1rem;
                }

                .column {
                    flex: 1;
                }
            `}</style>
        </div>
    );
}
